import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterViewInit, ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatTable } from '@angular/material/table';

import { fromEvent, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Directive()
export abstract class RdsResizeableTableComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;

  /** Map of user-customized column widths (in px) */
  @Input() set columnWidths(widths: { [id: string]: number }) {
    this._columnWidth = { ...this._columnWidth, ...widths };
  }
  /** Whether to enable column resize, defaults to false */
  @Input()
  get enableColumnResize(): boolean {
    return this._enableColumnResize;
  }
  set enableColumnResize(enable: boolean) {
    const bool = coerceBooleanProperty(enable);
    if (bool !== this._enableColumnResize) {
      this._enableColumnResize = bool;
      if (this.enableColumnResize && !!this.areColumnsSet) {
        this.initializeColumnWidths();
      }
      this.cdr.markForCheck();
    }
  }
  private _enableColumnResize: boolean;

  @Output() resize = new EventEmitter<{ [id: string]: number }>();

  /** Whether the columns have been set */
  abstract get areColumnsSet(): boolean;
  /** Minimum column width */
  abstract get minColWidth(): number;

  /** The table columns to render */
  displayedColumns: Array<string> = [];

  //#region Column Resize properties/functions
  /** Map of column IDs to column width */
  _columnWidth: { [id: string]: number } = {};
  /** Whether a column is being resized */
  _resizingColumn = false;
  /** ID of the column being resized */
  _currentResizeId: string;
  /** Horizontal coordinate where the mouse was clicked when starting to drag */
  _dragEventStartX: number;
  /** Width of column before it is resized */
  _columnStartWidth: number;
  _destroyed$ = new Subject();
  _mousemoveSub: Subscription;
  _mouseupSub: Subscription;
  //#endregion

  constructor(protected cdr: ChangeDetectorRef) {}

  ngAfterViewInit() {
    this.updateStickyColumnStyles();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Handle the `mousedown` event when a column is being resized via the drag handle
   * @param event mousedown `MouseEvent`
   * @param id ID of the column being resized
   */
  _onResizeColumn(event: MouseEvent, id: string) {
    event.preventDefault();
    event.stopPropagation();
    this._currentResizeId = id;
    this._resizingColumn = true;
    this._dragEventStartX = event.pageX;

    // Get the drag handle element from the event.target
    const targetElem: Element = event.target as Element;
    // set start width from table cell, which is the parent of the drag handle
    this._columnStartWidth = targetElem.parentElement.clientWidth;
    this._watchMouseDragMovement(id);
  }

  /**
   * Determine the initial column width for any column.
   * You may want to override this and add extra logic if you want to change the logic.
   * i.e. if you want ghost columns or numeric columns to have a smaller width
   *
   * @param id The ID of the column
   */
  protected getColumnWidth(id: string): number {
    return 150;
  }

  /**
   * Initialize columns widths when resize is enabled.
   * The displaytedColumns need to be set prior this initialization.
   */
  protected initializeColumnWidths() {
    this.displayedColumns.forEach((id) => {
      // If the width is not already set
      if (!(id in this._columnWidth)) {
        this._columnWidth[id] = this.getColumnWidth(id);
      }
    });
    this.cdr.markForCheck();
    this.updateStickyColumnStyles();
  }

  /** Updates the underlying CDK table's sticky column styles */
  protected updateStickyColumnStyles() {
    if (!!this.table && this.enableColumnResize) {
      setTimeout(() => this.table.updateStickyColumnStyles(), 0);
    }
  }

  /**
   * Update a column's width
   *
   * @param id ID of the column to update
   * @param width new width to set
   */
  private _updateColumnWidth(id: string, width: number) {
    // Get previous width
    const originalWidth = this._columnWidth[id];

    const changeInWidth = width - originalWidth;

    // If the width has changed, update it
    if (changeInWidth !== 0) {
      this._columnWidth[id] = width;
      this.cdr.markForCheck();
      this.updateStickyColumnStyles();
    }
  }

  /**
   * Listen for `mousemove` events to resize the column and listen for the `mouseup` event to stop.
   *
   * @param id ID of the column being resized
   */
  private _watchMouseDragMovement(id: string) {
    this._mousemoveSub = fromEvent(document, 'mousemove')
      .pipe(takeUntil(this._destroyed$), debounceTime(10), distinctUntilChanged())
      .subscribe((event: MouseEvent) => {
        // When the mouse is moving and the mouse button is held down
        if (this._resizingColumn && event.buttons) {
          // Determine the difference between when the mouse was clicked and where it has moved to
          const dx = event.pageX - this._dragEventStartX;
          // Calculate the new column width
          const width = this._columnStartWidth + dx;

          // Resize as long as it is above the minimum
          if (width > this.minColWidth) {
            this._updateColumnWidth(id, width);
          }
        }
      });

    // When the mouse is released stop listening for mouse movement; stop the resizing
    this._mouseupSub = fromEvent(document, 'mouseup')
      .pipe(takeUntil(this._destroyed$))
      .subscribe((event: MouseEvent) => {
        if (this._resizingColumn) {
          event.preventDefault();
          event.stopPropagation();
          this._resizingColumn = false;
          this._currentResizeId = null;
          this.cdr.markForCheck();
          this._mousemoveSub.unsubscribe();
          this._mouseupSub.unsubscribe();
          this.resize.emit({ [id]: this._columnWidth[id] });
        }
      });
  }
}
